import * as React from "react";
import { useState } from "react";
import ModalPopup from "../../commons/components/modal/ModalPopup";
import Form from "../../commons/components/form/Form";
import TextInput from "../../commons/components/form/input/TextInput";
import { maxLength, required } from "../../commons/model/form/Validators";
import sfetch from "../../commons/utils/super-fetch";
import InputState from "../../commons/model/form/InputState";
import { Tag } from "./Tag";
import "./TagModal.scss";
import { ModalType, TagModalStates } from "./TagModalTypeAndState";
import { PatientInformation } from "../../commons/model/patient/PatientInfo";
import { createRoot } from "react-dom/client";

interface TagModalProps {
  closeModal: (state: string) => void;
  fetchUrl: string;
  modalType: ModalType;
  tag?: Tag;
  onTagAdded?: (tag: Tag, patient: PatientInformation) => void;
  tagPrefilledValue?: string;
  patient?: PatientInformation;
}

export default function TagModal({
  closeModal,
  fetchUrl,
  modalType,
  tag,
  onTagAdded,
  tagPrefilledValue = "",
  patient,
}: TagModalProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [formErrorMessage, setFormErrorMessage] = useState<string>("");
  const [selectedColor] = useState<string>(tag?.color || "default");
  const labelMaxLength = 25;

  const getModalProperties = () => {
    if (modalType === ModalType.CREATE) {
      return {
        title: i18n["Page.Settings.Tags.Modal.Form.Title.Add"],
        submitLabel: i18n["Page.Settings.Tags.Modal.Form.ConfirmButton.Add"],
        inputValue: tagPrefilledValue,
        submitAction: (form: { [key: string]: InputState }) => createTag(form),
      };
    } else {
      return {
        title: i18n["Page.Settings.Tags.Modal.Form.Title.Update"],
        submitLabel: i18n["Page.Settings.Tags.Modal.Form.ConfirmButton.Update"],
        inputValue: tag.value,
        submitAction: (form: { [key: string]: InputState }) => updateTag(form),
      };
    }
  };

  const modalProps = getModalProperties();

  const createTag = (form: { [key: string]: InputState }): Promise<any> => {
    setFormErrorMessage("");
    setLoading(true);
    return sfetch(fetchUrl, {
      method: "POST",
      body: JSON.stringify({
        tagName: form.labelName.value,
        tagColor: selectedColor,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then(async (response) => {
        const tag: Tag = await response.json();
        onTagAdded && onTagAdded(tag, patient);
        closeModal(TagModalStates.SUCCESS);
      })
      .catch((response) => {
        if (response.status == 409) {
          setFormErrorMessage(i18n["Page.Settings.Tags.Modal.ErrorMessage"]);
          setLoading(false);
        } else {
          closeModal(TagModalStates.ERROR);
        }
        throw response;
      });
  };

  const updateTag = (form: { [key: string]: InputState }): Promise<any> => {
    setFormErrorMessage("");
    setLoading(true);
    return sfetch(fetchUrl + "/" + tag.uid, {
      method: "PUT",
      body: JSON.stringify({
        tagName: form.labelName.value,
        tagColor: selectedColor,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then(() => closeModal(TagModalStates.SUCCESS))
      .catch((response) => {
        if (response.status == 409) {
          setFormErrorMessage(i18n["Page.Settings.Tags.Modal.ErrorMessage"]);
          setLoading(false);
        } else {
          closeModal(TagModalStates.ERROR);
        }
        throw response;
      });
  };

  return (
    <ModalPopup
      closeModal={() => closeModal(TagModalStates.CLOSE)}
      id="modal-add-label"
      loading={loading}
    >
      <div className="title">{modalProps.title}</div>
      <Form
        submitLabel={modalProps.submitLabel}
        submit={(form) => modalProps.submitAction(form)}
        generalErrorMsg={formErrorMessage}
      >
        <TextInput
          name="labelName"
          label={i18n["Page.Settings.Tags.Modal.Form.InputLabel"]}
          placeholder={i18n["Page.Settings.Tags.Modal.Form.InputLabel"]}
          initialValue={modalProps.inputValue}
          validators={[
            maxLength(i18n["Page.Settings.Tags.Modal.ErrorMessage.MaxLength"], labelMaxLength),
            required(),
          ]}
        />
      </Form>
    </ModalPopup>
  );
}

TagModal.loadComponent = (elementId: string, props: TagModalProps) => {
  const container = document.getElementById(elementId);
  const root = createRoot(container!);
  root.render(<TagModal {...props} />);
};
